import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { ExternalLink, Pencil, Plus, Trash2 } from "lucide-react";

import { deleteComponent, getComponentStatus, listComponents } from "@/api/components";
import { CatalogHealthBadge } from "@/components/CatalogHealthBadge";
import { ConfirmModal } from "@/components/ConfirmModal";
import { DeleteError } from "@/components/DeleteError";
import { MenuDivider, MenuItem } from "@/components/KebabMenu";
import { RefreshFailed, loadFailure } from "@/components/Spinner";
import { ReadinessBadge } from "@/components/StatusBadge";
import { ButtonLink, EmptyState, Icon, Page, PageHeader, StatusPill, Table, type TableColumn } from "@/components/ui";
import { GROUP_LABEL, repositoryNameOf } from "@/lib/catalogComponents";
import { CATALOG } from "@/lib/sections";
import { COMPONENT_GROUPS, type GatewayComponent } from "@/types/api";

// gw-docker guide — a separate repo, not part of this app's routing. Kept as
// a constant for the same reason as DATAPLANE_REPO_URL in Builds.tsx.
const ADD_COMPONENT_GUIDE_URL =
  "https://github.com/pulsestation/pulsestation-gw-docker/blob/main/docs/AJOUTER_UN_COMPOSANT.md";

function componentDescription(component: GatewayComponent): string {
  const description = component.body.description;
  return typeof description === "string" && description ? description : "—";
}

function componentRef(component: GatewayComponent): string {
  return `${component.group}/${component.name}`;
}

/**
 * Un maillon rouge quelque part, sans dire lequel, a coûté trois heures de
 * diagnostic le 2026-08-28 (voir la page de détail, /status). Cette colonne
 * n'est qu'un aperçu — vert si tout est livrable, orange sinon, gris tant
 * que la réponse n'est pas là — la cause et le remède vivent sur la page
 * vers laquelle elle renvoie.
 *
 * Une requête par ligne, DÉLIBÉRÉMENT : contrairement à /usage (le
 * commentaire au-dessus de la requête plus bas), le coût dominant ici
 * (``enumerate_claimed_stages``) est borné par le nombre de tenants
 * réclamés, pas multiplié par le nombre de types qui utilisent la brique —
 * un ordre de grandeur en dessous des ~1500 requêtes DynamoDB évitées
 * là-bas. Si le parc de tenants ou le catalogue grossit significativement,
 * ce choix est à revisiter (endpoint de statut par lot, ou chargement
 * différé ligne par ligne).
 */
function ComponentStatusCell({
  group,
  name,
  token,
}: {
  group: string;
  name: string;
  token: string;
}) {
  const statusPath = `/catalog/components/${encodeURIComponent(group)}/${encodeURIComponent(name)}/status`;

  const query = useQuery({
    queryKey: ["component-status", group, name],
    queryFn: () => getComponentStatus(token, group, name),
    enabled: !!token,
    staleTime: 60_000,
  });

  if (query.isError) {
    return (
      <Link
        to={statusPath}
        title={query.error instanceof Error ? query.error.message : String(query.error)}
      >
        <StatusPill tone="neutral">Unknown</StatusPill>
      </Link>
    );
  }

  // Couvre à la fois le chargement initial et une requête pas encore
  // activée (token pas encore prêt) : ``query.data`` reste undefined dans
  // les deux cas, et c'est ce sur quoi on doit se garder plutôt que sur
  // ``isLoading`` seul (qui ne couvre pas une query désactivée).
  if (!query.data) {
    return (
      <Link to={statusPath}>
        <StatusPill tone="neutral">Checking…</StatusPill>
      </Link>
    );
  }

  const stages = query.data.stages;
  if (stages.length === 0) {
    return (
      <Link to={statusPath}>
        <StatusPill tone="neutral">No claimed stage</StatusPill>
      </Link>
    );
  }

  const deliverable = stages.every((s) => s.deliverable);
  return (
    <Link to={statusPath}>
      <ReadinessBadge
        ready={deliverable}
        readyLabel="Deliverable"
        pendingLabel="Attention needed"
      />
    </Link>
  );
}

export function CatalogComponentsPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const qc = useQueryClient();

  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState<GatewayComponent | null>(null);

  const query = useQuery({
    queryKey: ["catalog", "components"],
    queryFn: () => listComponents(token),
    enabled: !!token,
  });

  const components = query.data?.items ?? [];

  // No per-row usage lookup here, deliberately. One /usage call per
  // component looks like 21 HTTP requests; each one re-sweeps the whole
  // catalog and then queries every tenant's stages, which is on the order
  // of 1500 DynamoDB requests every time this page is opened. The number
  // is shown where it is actually needed and already loaded — the
  // component editor, right above the form that is about to change it.

  const deleteMut = useMutation({
    mutationFn: (component: GatewayComponent) =>
      deleteComponent(token, component.group, component.name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["catalog", "components"] });
      setDeleteTarget(null);
    },
  });

  const grouped = COMPONENT_GROUPS.map((group) => ({
    group,
    items: components
      .filter((c) => c.group === group)
      .sort((a, b) => a.name.localeCompare(b.name)),
  })).filter((g) => g.items.length > 0);

  const editPathOf = (component: GatewayComponent) =>
    `/catalog/components/${component.group}/${component.name}/edit`;

  const columns: TableColumn<GatewayComponent>[] = [
    {
      key: "name",
      header: "Name",
      cell: (component) => (
        <>
          <Link to={editPathOf(component)} className="font-mono text-brand-600 hover:underline">
            {component.name}
          </Link>
          {component.pendingImport && (
            <StatusPill
              tone="warning"
              className="ml-2"
              title="The last import proposed a change to inclusion or mandatory — open the component to review and accept it."
            >
              Pending review
            </StatusPill>
          )}
        </>
      ),
    },
    { key: "description", header: "Description", cell: (component) => componentDescription(component) },
    {
      key: "repository",
      header: "Image repository",
      mono: true,
      muted: true,
      cell: (component) => repositoryNameOf(component.body.image_uri) ?? "—",
    },
    {
      key: "inclusion",
      header: "Inclusion",
      muted: true,
      cell: (component) => (component.inclusion === "always" ? "Always included" : "Optional"),
    },
    {
      key: "status",
      header: "Status",
      cell: (component) => <ComponentStatusCell group={component.group} name={component.name} token={token} />,
    },
  ];

  // One set of columns, one menu and one empty state for every group's table.
  const tableParts = {
    columns,
    rowKey: componentRef,
    empty: (
      <EmptyState
        title="No components yet"
        action={
          <ButtonLink to="/catalog/components/new" variant="primary" icon={Plus}>
            New component
          </ButtonLink>
        }
      >
        Components are the building blocks that gateway types are composed of.
      </EmptyState>
    ),
    actions: {
      label: (component: GatewayComponent) => `Actions for ${componentRef(component)}`,
      items: (component: GatewayComponent) => (
        <>
          <MenuItem icon={Pencil} onClick={() => navigate(editPathOf(component))}>
            Edit
          </MenuItem>
          <MenuDivider />
          {/* The destructive gesture opens the confirmation, which shows the progress. */}
          <MenuItem danger icon={Trash2} disabled={deleteMut.isPending} onClick={() => setDeleteTarget(component)}>
            Delete…
          </MenuItem>
        </>
      ),
    },
  };

  return (
    <Page>
      <PageHeader
        section={CATALOG}
        title="Components"
        subtitle={
          <>
            Reusable building blocks composing the customer gateway types. Open
            one to see which types and tenants it reaches.
            <a
              href={ADD_COMPONENT_GUIDE_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-1 flex w-fit items-center gap-1 text-brand-600 hover:underline"
            >
              Adding a new component
              <Icon icon={ExternalLink} size="sm" />
            </a>
          </>
        }
        actions={
          <>
            <CatalogHealthBadge />
            <ButtonLink to="/catalog/components/new" variant="primary" icon={Plus}>
              New component
            </ButtonLink>
          </>
        }
      />

      <div className="space-y-8">
        <RefreshFailed query={query} />
        {query.data === undefined || components.length === 0 ? (
          <Table
            label="Components"
            {...tableParts}
            rows={query.data ? [] : undefined}
            error={loadFailure(query)}
          />
        ) : (
          grouped.map(({ group, items }) => (
            <div key={group}>
              <h2 className="mb-2 text-sm font-semibold text-ink">{GROUP_LABEL[group]}</h2>
              <Table label={GROUP_LABEL[group]} {...tableParts} rows={items} />
            </div>
          ))
        )}
      </div>

      {deleteTarget && (
        <ConfirmModal
          title="Delete component"
          confirmLabel="Delete"
          pendingLabel="Deleting…"
          danger
          pending={deleteMut.isPending}
          // Inside the modal, not on the page: the overlay hides the page, and a
          // refused delete (409) must say which types block it.
          error={deleteMut.error}
          errorView={(error) => (
            <DeleteError
              error={error}
              subject="This component"
              noun="gateway types"
              blockers={(details) =>
                Array.isArray(details?.types)
                  ? (details.types as { model: string; version: string }[]).map((t) => `${t.model}@${t.version}`)
                  : []
              }
              remedy="Remove it from those types before retrying."
            />
          )}
          onCancel={() => {
            deleteMut.reset();
            setDeleteTarget(null);
          }}
          onConfirm={() => deleteMut.mutate(deleteTarget)}
        >
          <p>
            Delete <code className="font-mono">{componentRef(deleteTarget)}</code>? This
            action cannot be undone.
          </p>
        </ConfirmModal>
      )}
    </Page>
  );
}
